import { useCallback, useState } from 'react';

/**
 * State that survives a reload.
 *
 * For interface preferences only (which sidebar sections are open, whether it is
 * collapsed). Never for anything that belongs to the server, and never for anything
 * sensitive: this is `localStorage`, readable by any script on the origin.
 *
 * Reads and writes are wrapped because storage throws in private-browsing modes and
 * when a quota is exceeded. A preference that cannot be saved is not worth an error.
 */
export function usePersistentState<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored === null ? initial : (JSON.parse(stored) as T);
    } catch {
      return initial;
    }
  });

  const update = useCallback(
    (next: T | ((current: T) => T)) => {
      setValue((current) => {
        const resolved = typeof next === 'function' ? (next as (c: T) => T)(current) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // Preference lost on reload; nothing else is affected.
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, update] as const;
}
